import { useState } from "react";

interface Props {
  email: string;
  isEn: boolean;
}

const AURIA_SERVICE_EMAIL = "calendario@auria-receptionist.ai";

const STEPS_ES = [
  {
    icon: "ri-calendar-2-line",
    title: "Abre Google Calendar en tu ordenador",
    body: "Ve a calendar.google.com y asegúrate de estar con la cuenta de Google que usas para tu negocio (no tu cuenta personal si son distintas).",
    action: { label: "Abrir Google Calendar", href: "https://calendar.google.com" },
  },
  {
    icon: "ri-settings-3-line",
    title: "Entra en la configuración del calendario",
    body: "Haz clic en el icono de rueda dentada (⚙️) arriba a la derecha → selecciona \"Configuración\".",
  },
  {
    icon: "ri-list-check-2",
    title: "Selecciona tu calendario de negocio",
    body: "En el panel izquierdo, bajo \"Mis calendarios\", haz clic en el nombre del calendario donde guardas tus citas. Si usas el calendario principal, haz clic en tu nombre.",
  },
  {
    icon: "ri-share-line",
    title: "Ve a \"Compartir con otras personas\"",
    body: "Desplázate hacia abajo hasta encontrar la sección \"Compartir con otras personas específicas\". Haz clic en el botón \"+Agregar personas\".",
  },
  {
    icon: "ri-mail-add-line",
    title: "Añade el email de Auria",
    body: `Introduce la siguiente dirección exacta y pulsa Enter:`,
    highlight: AURIA_SERVICE_EMAIL,
    sub: "En el menú desplegable de permisos, selecciona \"Hacer cambios en eventos\". Luego pulsa Enviar.",
  },
];

const STEPS_EN = [
  {
    icon: "ri-calendar-2-line",
    title: "Open Google Calendar on your computer",
    body: "Go to calendar.google.com and make sure you're signed in with the Google account you use for your business.",
    action: { label: "Open Google Calendar", href: "https://calendar.google.com" },
  },
  {
    icon: "ri-settings-3-line",
    title: "Go to Calendar Settings",
    body: "Click the gear icon (⚙️) in the top right → select \"Settings\".",
  },
  {
    icon: "ri-list-check-2",
    title: "Select your business calendar",
    body: "In the left panel, under \"My calendars\", click the name of the calendar where you save your appointments.",
  },
  {
    icon: "ri-share-line",
    title: "Go to \"Share with specific people\"",
    body: "Scroll down to find the \"Share with specific people\" section. Click the \"+ Add people\" button.",
  },
  {
    icon: "ri-mail-add-line",
    title: "Add Auria's email address",
    body: "Enter the following exact address and press Enter:",
    highlight: AURIA_SERVICE_EMAIL,
    sub: "In the permissions dropdown, select \"Make changes to events\". Then click Send.",
  },
];

interface StepData {
  icon: string;
  title: string;
  body: string;
  highlight?: string;
  sub?: string;
  action?: { label: string; href: string };
}

interface ModalProps {
  isEn: boolean;
  onConnect: () => void;
  onClose: () => void;
}

function ConnectionModal({ isEn, onConnect, onClose }: ModalProps) {
  const [step, setStep] = useState(0);
  const STEPS: StepData[] = isEn ? STEPS_EN : STEPS_ES;
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-stone-900 px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">
              <i className="ri-calendar-check-line text-white text-lg" />
            </div>
            <div>
              <p className="text-xs font-bold text-stone-400 uppercase tracking-widest">
                {isEn ? "Google Calendar" : "Google Calendar"}
              </p>
              <p className="text-sm font-bold text-white">
                {isEn ? "Connect your calendar" : "Conecta tu calendario"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-sm" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-6 pt-5 pb-0">
          <div className="flex items-center gap-1.5 mb-5">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i < step ? "bg-emerald-400 flex-1" : i === step ? "bg-amber-500 flex-[2]" : "bg-stone-200 flex-1"
                }`}
              />
            ))}
          </div>
          <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-1">
            {isEn ? `Step ${step + 1} of ${STEPS.length}` : `Paso ${step + 1} de ${STEPS.length}`}
          </p>
        </div>

        {/* Step content */}
        <div className="px-6 pt-3 pb-5 min-h-[200px]">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
              <i className={`${current.icon} text-amber-500 text-lg`} />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold text-stone-900 mb-2 leading-snug">{current.title}</h3>
              <p className="text-sm text-stone-500 leading-relaxed">{current.body}</p>
              {current.highlight && (
                <div className="mt-3 flex items-center gap-2 px-3 py-2.5 bg-stone-900 rounded-xl">
                  <i className="ri-mail-line text-amber-400 text-sm flex-shrink-0" />
                  <code className="text-amber-400 text-sm font-mono font-bold tracking-wide select-all">
                    {current.highlight}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(current.highlight!)}
                    className="ml-auto w-7 h-7 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-stone-400 hover:text-white transition-colors cursor-pointer flex-shrink-0"
                    title={isEn ? "Copy" : "Copiar"}
                  >
                    <i className="ri-clipboard-line text-xs" />
                  </button>
                </div>
              )}
              {current.sub && (
                <p className="text-sm text-stone-500 leading-relaxed mt-2">{current.sub}</p>
              )}
              {current.action && (
                <a
                  href={current.action.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-xl text-xs font-semibold text-stone-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line text-xs" />
                  {current.action.label}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 border-t border-stone-100 pt-4 flex items-center gap-3">
          {step > 0 && (
            <button
              onClick={() => setStep((s) => s - 1)}
              className="flex items-center gap-2 px-4 py-2.5 border border-stone-200 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-sm" />
              {isEn ? "Back" : "Atrás"}
            </button>
          )}
          <div className="flex-1" />
          {isLast ? (
            <button
              onClick={onConnect}
              className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-double-line text-sm" />
              {isEn ? "Connected — done!" : "Listo, he completado los pasos"}
            </button>
          ) : (
            <button
              onClick={() => setStep((s) => s + 1)}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-white rounded-xl text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
            >
              {isEn ? "Next step" : "Siguiente paso"}
              <i className="ri-arrow-right-line text-sm" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GoogleCalendarWidget({ email, isEn }: Props) {
  const storageKey = `auria_cal_${email}`;
  const [connected, setConnected] = useState(() => {
    try { return localStorage.getItem(storageKey) === "true"; } catch { return false; }
  });
  const [showModal, setShowModal] = useState(false);
  const [justConnected, setJustConnected] = useState(false);

  const handleConnect = () => {
    setConnected(true);
    setJustConnected(true);
    setShowModal(false);
    try { localStorage.setItem(storageKey, "true"); } catch { /* ignore */ }
    setTimeout(() => setJustConnected(false), 4000);
  };

  const handleDisconnect = () => {
    setConnected(false);
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
  };

  return (
    <>
      {showModal && (
        <ConnectionModal isEn={isEn} onConnect={handleConnect} onClose={() => setShowModal(false)} />
      )}

      <div className={`rounded-2xl border p-5 transition-all duration-300 ${connected ? "bg-emerald-50 border-emerald-100" : "bg-white border-stone-100"}`}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${connected ? "bg-emerald-100" : "bg-stone-100"}`}>
              <i className={`ri-calendar-2-line text-base ${connected ? "text-emerald-600" : "text-stone-500"}`} />
            </div>
            <div>
              <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-0.5">
                {isEn ? "Integration" : "Integración"}
              </p>
              <p className="text-sm font-bold text-stone-800">Google Calendar</p>
            </div>
          </div>
          {connected && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-100 rounded-full flex-shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse" />
              <span className="text-xs font-bold text-emerald-700">
                {isEn ? "Connected" : "Conectado"}
              </span>
            </span>
          )}
        </div>

        {justConnected && (
          <div className="mb-3 px-3 py-2.5 bg-emerald-100 rounded-xl flex items-center gap-2">
            <i className="ri-checkbox-circle-fill text-emerald-600 text-sm" />
            <p className="text-xs font-semibold text-emerald-700">
              {isEn ? "Calendar connected! Auria can now book appointments in real time." : "¡Calendario conectado! Auria ya puede agendar citas en tiempo real."}
            </p>
          </div>
        )}

        {connected ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              {(isEn ? [
                "Auria checks availability before booking",
                "Appointments sync automatically",
                "Reminders sent to your clients",
              ] : [
                "Auria comprueba disponibilidad antes de agendar",
                "Las citas se sincronizan automáticamente",
                "Recordatorios enviados a tus clientes",
              ]).map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <i className="ri-check-line text-emerald-500 text-xs" />
                  <span className="text-xs text-stone-600">{item}</span>
                </div>
              ))}
            </div>
            <button
              onClick={handleDisconnect}
              className="text-xs text-stone-400 hover:text-rose-500 transition-colors cursor-pointer flex items-center gap-1 mt-1"
            >
              <i className="ri-link-unlink-m text-xs" />
              {isEn ? "Disconnect calendar" : "Desconectar calendario"}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-stone-500 leading-relaxed">
              {isEn
                ? "Connect your Google Calendar so Auria can check real-time availability and book appointments directly."
                : "Conecta tu Google Calendar para que Auria compruebe disponibilidad en tiempo real y agende citas directamente."}
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-stone-900 hover:bg-stone-700 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-line text-sm" />
              {isEn ? "Connect Google Calendar" : "Conectar Google Calendar"}
              <i className="ri-arrow-right-line text-xs" />
            </button>
            <p className="text-xs text-stone-400 text-center">
              {isEn ? "Takes 2 minutes — no technical knowledge needed" : "2 minutos · sin conocimientos técnicos"}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
